import React, { useState } from 'react';
import axios from 'axios';
import { useLicense } from '../contexts/LicenseContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { KeyRound, Copy, ShieldAlert, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LicenseBlocker = ({ children }) => {
  const { isBlocked, status, refresh, loading } = useLicense();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [cnpj, setCnpj] = useState('');
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // A tela de login e a rota do master ficam SEMPRE acessíveis (senão o admin não consegue nem entrar)
  if (['/login', '/master', '/entrar', '/super-admin'].includes(location.pathname)) {
    return children;
  }

  if (loading) return children;
  if (!isBlocked) return children;

  const fp = status?.fingerprint || '---';
  const cnpjSet = !!status?.cnpj;

  const copyFingerprint = async () => {
    try { await navigator.clipboard.writeText(fp); } catch (e) { /* clipboard não disponível */ }
  };

  const saveCnpj = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await axios.post(`${API}/license/cnpj`, { cnpj }, { withCredentials: true });
      await refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar CNPJ');
    } finally { setBusy(false); }
  };

  const activate = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await axios.post(`${API}/license/activate`, { license_blob: key.trim() }, { withCredentials: true });
      setKey('');
      await refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao ativar licença');
    } finally { setBusy(false); }
  };

  // Se o usuário não estiver logado ou não for admin, mostra mensagem simples
  const isAdmin = user && (user.role === 'admin' || user.role === 'manager');

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-slate-800 border border-slate-700 rounded-lg shadow-2xl overflow-hidden" data-testid="license-blocker">
        <div className="bg-red-600 p-4 flex items-center gap-3">
          <ShieldAlert size={32} />
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{status?.revoked ? 'Sistema Bloqueado — Licença Desativada' : 'Sistema Bloqueado — Renovar Licença'}</h1>
            <p className="text-red-100 text-sm" data-testid="license-block-reason">{status?.revoked ? 'Esta licença foi desativada pelo fornecedor. Entre em contato com o suporte para regularizar.' : 'O Smart Flow precisa de uma chave de liberação válida para operar.'}</p>
          </div>
          {user && (
            <Button variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-red-700" data-testid="license-logout-btn">
              <LogOut size={16} className="mr-1" /> Sair
            </Button>
          )}
        </div>
        <div className="p-6 space-y-5">
          {!user ? (
            <Alert>
              <AlertDescription>Faça login como administrador para ativar/renovar a licença. <a href="/login" className="underline text-blue-300">Ir para login</a></AlertDescription>
            </Alert>
          ) : !isAdmin ? (
            <Alert variant="destructive">
              <AlertDescription>Só o administrador pode ativar a licença. Chame o dono/gerente da loja.</AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Passo 1: CNPJ */}
              {!cnpjSet && (
                <form onSubmit={saveCnpj} className="space-y-2 bg-slate-900 rounded p-4 border border-slate-700" data-testid="license-cnpj-form">
                  <Label className="text-slate-100">Passo 1 — Cadastre o CNPJ da sua empresa</Label>
                  <p className="text-xs text-slate-400">O CNPJ é usado para gerar o fingerprint único da sua instalação.</p>
                  <div className="flex gap-2">
                    <Input value={cnpj} onChange={(e) => setCnpj(e.target.value)} placeholder="00.000.000/0001-00" className="bg-slate-950 border-slate-600 text-slate-100" data-testid="license-cnpj-input" required />
                    <Button type="submit" disabled={busy || cnpj.replace(/\D/g,'').length < 11} data-testid="license-cnpj-save">
                      Salvar CNPJ
                    </Button>
                  </div>
                </form>
              )}

              {/* Passo 2: Fingerprint (para copiar e enviar ao fornecedor) */}
              {cnpjSet && (
                <div className="bg-slate-900 rounded p-4 border border-slate-700">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-slate-100">Passo 2 — Envie este código ao fornecedor</Label>
                    <Button variant="ghost" size="sm" onClick={copyFingerprint} className="text-blue-300 hover:text-blue-100 hover:bg-slate-800" data-testid="license-copy-fp">
                      <Copy size={14} className="mr-1" /> Copiar
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">Este é o &quot;carimbo&quot; da sua máquina. Cada máquina tem um único.</p>
                  <div className="bg-black rounded p-3 font-mono text-lg text-emerald-400 tracking-widest text-center select-all" data-testid="license-fingerprint">
                    {fp}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-2">CNPJ vinculado: {status?.cnpj || '—'}</p>
                </div>
              )}

              {/* Passo 3: Colar chave */}
              {cnpjSet && (
                <form onSubmit={activate} className="space-y-2 bg-slate-900 rounded p-4 border border-slate-700" data-testid="license-activate-form">
                  <Label className="text-slate-100">Passo 3 — Cole a chave recebida do fornecedor</Label>
                  <textarea
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    rows={5}
                    placeholder="Cole aqui a chave que o fornecedor te enviou..."
                    className="w-full bg-slate-950 border border-slate-600 rounded p-2 text-slate-100 text-xs font-mono"
                    data-testid="license-key-input"
                    required
                  />
                  {error && <p className="text-sm text-red-400 font-semibold" data-testid="license-error">{error}</p>}
                  <div className="flex gap-2">
                    <Button type="submit" disabled={busy || key.trim().length < 20} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="license-activate-btn">
                      <KeyRound size={16} className="mr-2" /> {busy ? 'Ativando...' : 'Ativar Licença'}
                    </Button>
                  </div>
                </form>
              )}

              {status?.expires_at && (
                <p className="text-xs text-center text-slate-400">
                  Última licença expirou em: {new Date(status.expires_at).toLocaleDateString('pt-BR')}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
